/**
 * GA4 Analytics API service — Google Analytics engagement endpoints.
 */
import { apiClient } from './apiClient';
import { useTenantStore } from '@/stores/useTenantStore';

const getTenantId = () => useTenantStore.getState().currentTenant;

/**
 * GA4 endpoints signal "not configured / upstream failure" with a 200 +
 * `{ error: string }` body. Treat that as a failure so React Query surfaces
 * isError instead of the page rendering it as "no data".
 */
function throwIfErrorBody(data: unknown): void {
  if (
    data !== null &&
    typeof data === 'object' &&
    typeof (data as { error?: unknown }).error === 'string'
  ) {
    throw new Error((data as { error: string }).error);
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GA4PageMetric {
  page_path: string;
  page_title: string;
  page_views: number;
  sessions: number;
  avg_time: number;
  bounce_rate: number;
}

export interface GA4PageMetricsResponse {
  pages: GA4PageMetric[];
  total: number;
  limit: number;
  offset: number;
}

export interface GA4Aggregate {
  total_page_views: number;
  total_sessions: number;
  avg_time_on_page: number;
  bounce_rate: number;
  new_users: number;
  returning_users: number;
  page_views_change_pct: number;
  sessions_change_pct: number;
  time_on_page_change: number;
  bounce_rate_change: number;
}

export interface GA4TrafficSource {
  source: string;
  sessions: number;
  percentage: number;
}

export interface GA4DailyMetric {
  date: string;
  page_views: number;
  sessions: number;
}

export interface GA4Property {
  account_name: string;
  property_id: string;
  property_name: string;
}

export type Period = '7d' | '28d' | '90d';

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

export async function fetchGA4PageMetrics(
  tenantId: string | null | undefined,
  period: Period = '28d',
  limit: number = 50,
  offset: number = 0,
): Promise<GA4PageMetricsResponse> {
  const { data } = await apiClient.get<GA4PageMetricsResponse>(
    '/ga4/page-metrics',
    { params: { tenant_id: tenantId ?? getTenantId(), period, limit, offset } },
  );
  throwIfErrorBody(data);
  return data;
}

export async function fetchGA4Aggregate(
  tenantId: string | null | undefined,
  period: Period = '28d',
): Promise<GA4Aggregate> {
  const { data } = await apiClient.get<GA4Aggregate>(
    '/ga4/aggregate',
    { params: { tenant_id: tenantId ?? getTenantId(), period } },
  );
  throwIfErrorBody(data);
  return data;
}

export async function fetchGA4TrafficSources(
  tenantId: string | null | undefined,
  period: Period = '28d',
): Promise<GA4TrafficSource[]> {
  const { data } = await apiClient.get<GA4TrafficSource[]>(
    '/ga4/traffic-sources',
    { params: { tenant_id: tenantId ?? getTenantId(), period } },
  );
  throwIfErrorBody(data);
  return data;
}

export async function fetchGA4Daily(
  tenantId: string | null | undefined,
  period: Period = '28d',
): Promise<GA4DailyMetric[]> {
  const { data } = await apiClient.get<GA4DailyMetric[]>(
    '/ga4/daily',
    { params: { tenant_id: tenantId ?? getTenantId(), period } },
  );
  throwIfErrorBody(data);
  return data;
}

export async function fetchGA4Properties(): Promise<GA4Property[]> {
  const { data } = await apiClient.get<{ properties: GA4Property[] }>(
    '/ga4/properties',
  );
  return data.properties;
}
